/**
 * This is the entry point for the webpack bundle.
 *
 * Import any libraries needed across the whole application. Define navigation
 * routes and import the top-level component for each route.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import {
  HashRouter as Router,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { CacheLocation, FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';
import { defaultEndpoint, defaultScriptUrlPattern } from '@fingerprintjs/fingerprintjs-pro';
import FeatureFlagProvider from '~/components/FeatureFlagProvider';

import NewStyles from './newStyles';

import store from './store';
import NewApp from '~/containers/App/newApp';
import NameOnlyApp from '~/containers/App/nameOnlyApp';

import { featureOn } from '~/utils/featureToggle';
import { Feature } from '~/types/common';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

Sentry.init({
  dsn: APP.SENTRY_DNS,
  tunnel: APP.SENTRY_TUNNEL,
  environment: ENV.NODE_ENV,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/sapi\.oneroomstreaming\.com\/api/,
    /^https:\/\/sapi2\.oneroomstreaming\.com\/api/,
    /^https:\/\/sanalytic\.oneroomstreaming\.com\/api/,
    /^https:\/\/api\.oneroomstreaming\.com\/api/,
    /^https:\/\/api2\.oneroomstreaming\.com\/api/,
    /^https:\/\/analytic\.oneroomstreaming\.com\/api/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const rootElement = document.getElementById('app');
const root = createRoot(rootElement);

const queryClient = new QueryClient();

const CACHE_TIME = 60 * 60 * 24;

const Main = () => {
  const FORCE_REGISTRATION_ENABLED = featureOn(Feature.force_registration);
  
  return (
    <React.Fragment>
      <NewStyles />
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <FeatureFlagProvider>
            <CookiesProvider defaultSetOptions={{ path: '/' }}>
              <Router>
                {FORCE_REGISTRATION_ENABLED ? (
                  <FpjsProvider
                    cacheLocation={CacheLocation.LocalStorage}
                    cachePrefix='ONEROOM_VIEWER'
                    cacheTimeInSeconds={CACHE_TIME}
                    loadOptions={{
                      apiKey: APP.FINGERPRINT_KEY,
                      scriptUrlPattern: [
                        `https://id.oneroomstreaming.com/${APP.FPJS_BEHAVIOR_PATH}/${APP.FPJS_AGENT_DOWNLOAD_PATH}?apiKey=${APP.FINGERPRINT_KEY}&version=3&loaderVersion=3.8.4`,
                        defaultScriptUrlPattern, // Fallback to default CDN in case of error
                      ],
                      endpoint: [
                        `https://id.oneroomstreaming.com/${APP.FPJS_BEHAVIOR_PATH}/${APP.FPJS_GET_RESULT_PATH}?region=us-east-1`,
                        defaultEndpoint, // Fallback to default endpoint in case of error
                      ],
                    }}>
                    <NewApp />
                  </FpjsProvider>
                ) : (
                  <NameOnlyApp />
                )}
              </Router>
            </CookiesProvider>
          </FeatureFlagProvider>
        </Provider>
      </QueryClientProvider>
    </React.Fragment>
  );
};

root.render(<Main />);
